import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setCustomer, setModalOrderCustomer } from '../../../../redux/action/customer'
import { setAppEventZIndex, setAppModal, setAppModalData } from '../../../../redux/action/system'
import { useNavigate } from 'react-router-dom'
import { registerOrderThunk, setOrderStatusThunk } from '../../../../redux/thunk/order/setOrderStatus'
import { setShowSingleDriver } from '../../../../redux/action/driver-monitor'
import { restoreOrderThunk } from '../../../../redux/thunk/order/processUser'
import { setDeliveryCartCurrentDeliveryCost, setDeliveryCartItemFocusIndex, setDeliveryCartItems, setDeliveryCartOrder, setDeliveryCartOrderType } from '../../../../redux/action/deliveryCart'
import ButtonsModal from '../../../Elements/modal/ButtonsModal'
import { selectContainsAnyRole, selectHasAnyGroup, selectUsername } from '../../../../redux/selector/system'
import { getCouponThunk } from '../../../../redux/thunk/coupon'
import { convert2CouponItem, reorderButtonsForUnpaidOrder, roundPrice } from '../../../../helper'
import { storeWoltDriverDeliveryPromisesThunk } from '../../../../redux/thunk/woltDriverIntegrations'
import { checkIsTodayOrder } from '../../../../helper/checkIsTodayOrder'
import { getExtraDiscountPrice } from '../../../../redux/selector/cart/helper'
import { setCartExtraDiscount } from '../../../../redux/action/cartDiscount'
import { ENTERTAINMENT_EXPENSES_RECEIPT_MIN_SUM } from '../../../Elements/modal/PayerDataModal/constants'
import { ORDER_RESTORE_HIDDEN_COUPON_TYPES } from '../../../Elements/modal/order/Order/constants'
import { printRequestsThunk } from '../../../../redux/thunk/order/printRequests'
import { setSelectedOrder } from '../../../../redux/action/order'
import { prepareItemsForCoupon } from '../../../../helper/prepareItemsForCoupon'
import { useMarkReadyButton } from '../../../Elements/modal/order/MarkReadyButton/hooks'
import { isFullyPaidOrder } from '../../../Elements/table/OrdersTable/helper'
import { useCancellationWindow } from '../../../../hooks/useCancellationWindow'
import { useOpenOrderInvoice } from '../../../Elements/table/InvoicesTable/hooks/useOpenOrderInvoice'
import { useOrderInvoiceButtons } from '../../../Elements/table/InvoicesTable/hooks/useOrderInvoiceButtons'
import InvoiceCustomerDataModal from '../../../Elements/cart/InvoiceModals/InvoiceCustomerDataModal'

const DRIVER_CASH_PAYMENT_METHODS = ['CASH', 'EC_AT_DOORSTEP']

const EVENT_Z_INDEX = 1

const CustomerOrderButtonsModal = React.memo(() => {
	const { t } = useTranslation()
	const selectedOrder = useSelector(store => store.order.selectedOrder)
	const showOrder = useSelector(store => store.customerStore.modal.showOrder)
	const hasRoleServiceWrite = useSelector(selectContainsAnyRole(['DELIVERY_SERVICE_WRITE']))
	const hasRoleCancelOrder = useSelector(selectHasAnyGroup(['OWNER', 'SUPERADMIN', 'ADMIN', 'CALL_AGENT']))
	const username = useSelector(selectUsername)
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const isDelivery = selectedOrder?.orderType === 'DELIVERY'
	const isDeliveryByPlatform = selectedOrder?.orderType === 'DELIVERY_BY_PLATFORM'
	const partnerDeliveryMetaData = selectedOrder?.deliveryMetaData?.partner
	const isTakeawayDrive = partnerDeliveryMetaData?.type === 'TAKEAWAY_DRIVE'
	const isFoodoraDrive = partnerDeliveryMetaData?.type === 'FOODORA_DRIVE'
	const updatedDeliveryByPlatform = !!(isDelivery && partnerDeliveryMetaData?.type && (partnerDeliveryMetaData?.pickupEta || isTakeawayDrive))
	const isPlatformDriverOrder = isDeliveryByPlatform || updatedDeliveryByPlatform
	const isPartnerDriverOrder = partnerDeliveryMetaData?.deliveryId
	const isDeliveryPromiseId = partnerDeliveryMetaData?.deliveryPromiseId
	const showPayerDataModalForReceipt = roundPrice(selectedOrder?.totalPrice) >= ENTERTAINMENT_EXPENSES_RECEIPT_MIN_SUM
	const isPaid = selectedOrder?.payments?.filter(item => item?.method === selectedOrder?.paymentMethod)[0]?.payed
	const isInvoiceOrder = selectedOrder?.payments?.find(p => p.method === 'INVOICE')
	const deferredPayment = selectedOrder?.payments?.find(payment => payment.deferred)
	const isDriverCardPaymentPending = !isPaid && isDelivery && deferredPayment?.method === 'CREDIT_CARD_AT_DOORSTEP'
	const isDriverCashPaymentPending = !isPaid && isDelivery && DRIVER_CASH_PAYMENT_METHODS.includes(deferredPayment?.method)
	const isDriverPaymentPending = isDriverCardPaymentPending || isDriverCashPaymentPending
	const markReadyButton = useMarkReadyButton(selectedOrder, showOrder)
	const { canCancelOrder } = useCancellationWindow()

	const printOrderClick = useCallback(async () => {
		if (!selectedOrder.orderId) return
		if (isPaid) {
			await dispatch(printRequestsThunk(selectedOrder.orderId, 'CUSTOMER_RECEIPT_DUPLICATE', false))
			await dispatch(printRequestsThunk(selectedOrder.orderId, 'KITCHEN_RECEIPT'))
		} else {
			await dispatch(printRequestsThunk(selectedOrder.orderId, 'PRELIMINARY_RECEIPT'))
		}
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}, [dispatch, selectedOrder?.orderId, isPaid])

	const printCanceledOrderClick = useCallback(() => {
		if (!selectedOrder.orderId) return
		dispatch(setModalOrderCustomer({ showPrintCanceledOrder: true }))
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}, [dispatch, selectedOrder?.orderId])

	const handleClose = useCallback(() => {
		dispatch(setModalOrderCustomer({ showOrder: false }))
		dispatch(setAppEventZIndex(0))
	}, [dispatch])

	const { openOrderInvoice } = useOpenOrderInvoice(handleClose)
	const { goToInvoiceButton, createInvoiceButton, recipientModalProps } = useOrderInvoiceButtons({ order: selectedOrder, isOpen: !!(showOrder && selectedOrder), openOrderInvoice, zIndex: EVENT_Z_INDEX })

	const showOrderDetails = useCallback(() => {
		dispatch(setModalOrderCustomer({ showOrderData: true }))
	}, [dispatch])

	const cancelOrder = useCallback(() => {
		dispatch(setModalOrderCustomer({ showOrder: false }))
		dispatch(setModalOrderCustomer({ showCancelOrder: true }))
	}, [dispatch])

	const abortOrder = () => {
		dispatch(setModalOrderCustomer({ showOrder: false }))
		dispatch(setModalOrderCustomer({ showAbortOrder: true }))
	}

	const completeOrder = useCallback(async () => {
		await dispatch(setOrderStatusThunk(selectedOrder, 'COMPLETED'))
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}, [dispatch, selectedOrder])

	const confirmOrder = useCallback(() => {
		if (selectedOrder.preOrder && !isPlatformDriverOrder) {
			dispatch(setAppModal({ confirmPreOrderModalShow: true }))
			dispatch(setAppModalData({ confirmPreOrderModalData: [selectedOrder] }))
		} else if (isPlatformDriverOrder) {
			dispatch(setAppModal({ platformDriversOrderModalShow: true }))
			dispatch(setAppModalData({ platformDriversOrderModalData: [selectedOrder] }))
		} else if (selectedOrder.expressDelivery || selectedOrder?.expectedPickupTime) {
			dispatch(setAppModal({ confirmExpressOrderModalShow: true }))
			dispatch(setAppModalData({ confirmExpressOrderModalData: [selectedOrder] }))
		} else {
			dispatch(setAppModal({ processingTimeOrderModalShow: true }))
			dispatch(setAppModalData({ processingTimeOrderModalData: [selectedOrder] }))
		}
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}, [dispatch, isPlatformDriverOrder, selectedOrder])

	const registerOrder = useCallback(async () => {
		await dispatch(registerOrderThunk(selectedOrder.orderId))
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}, [dispatch, selectedOrder?.orderId])

	const convertItemsWithCoupon = useCallback(async (items) => {
		const { resultItems, marketingCoupon } = prepareItemsForCoupon(items)
		if (marketingCoupon) {
			const coupon = await dispatch(getCouponThunk(marketingCoupon.itemId))
			const convertItem = convert2CouponItem(coupon, marketingCoupon.status)
			return [...resultItems, convertItem]
		}
		return resultItems
	}, [dispatch])

	const changeOrder = useCallback(async () => {
		const categoryDiscountItems = (selectedOrder.purchaseItems || []).filter(item => item.type === 'CATEGORY_DISCOUNT')
		const preparedItems = await convertItemsWithCoupon([...selectedOrder.items, ...categoryDiscountItems])
		const customer = selectedOrder.customer ? { ...selectedOrder.customer, deliveryCost: selectedOrder.deliveryCost?.cost } : null
		dispatch(setModalOrderCustomer({ showOrder: false }))
		dispatch(setCustomer(customer))
		const extraDiscount = getExtraDiscountPrice(selectedOrder.items)
		dispatch(setCartExtraDiscount(Math.abs(extraDiscount)))
		dispatch(setDeliveryCartOrderType(selectedOrder.orderType))
		dispatch(setDeliveryCartOrder(selectedOrder))
		dispatch(setDeliveryCartItems(preparedItems))
		dispatch(setDeliveryCartItemFocusIndex(null))
		dispatch(setDeliveryCartCurrentDeliveryCost(selectedOrder.deliveryCost?.cost))
		navigate('/delivery/cart')
	}, [dispatch, selectedOrder, convertItemsWithCoupon, navigate])

	const openSingleDriverModal = useCallback(() => {
		dispatch(setShowSingleDriver(true))
	}, [dispatch])

	const handlePayment = useCallback(() => {
		dispatch(setModalOrderCustomer({ showOrder: false }))
		dispatch(setDeliveryCartOrder(selectedOrder))
		dispatch(setModalOrderCustomer({ showPaymentMethod: true }))
	}, [dispatch, selectedOrder])

	const changePayment = useCallback(() => {
		if (isInvoiceOrder) {
			dispatch(setModalOrderCustomer({ showOrder: false }))
			dispatch(setModalOrderCustomer({ showChangePaymentConfirm: true }))
		} else {
			handlePayment()
		}
	}, [dispatch, isInvoiceOrder, handlePayment])

	const restoreOrder = useCallback(async () => {
		const data = { printerMetaData: { userPrintRequest: { userName: username } } }
		await dispatch(restoreOrderThunk(selectedOrder.orderId, data))
		handleClose()
	}, [dispatch, selectedOrder?.orderId, username, handleClose])

	const handlePrintEntertainmentExpensesReceipt = async () => {
		if (showPayerDataModalForReceipt) {
			dispatch(setModalOrderCustomer({ showReceiptPayerData: true }))
		} else {
			await dispatch(printRequestsThunk(selectedOrder.orderId, 'ENTERTAINMENT_EXPENSES_RECEIPT'))
			dispatch(setSelectedOrder(null))
		}
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}

	const passOrderToPlatformCourier = useCallback(async () => {
		await dispatch(setOrderStatusThunk(selectedOrder, 'ON_THE_WAY'))
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}, [dispatch, selectedOrder])

	const passOrderToAnotherCourier = useCallback(async () => {
		await dispatch(storeWoltDriverDeliveryPromisesThunk(selectedOrder))
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}, [dispatch, selectedOrder])

	const canBeRegisteredNow = useMemo(() => {
		if (!selectedOrder) return false
		const orderDate = selectedOrder.preOrder ? new Date(selectedOrder.preOrder) : new Date(selectedOrder.orderDate)
		const now = new Date()
		orderDate.setUTCHours(5, 0, 0, 0)
		return now > orderDate
	}, [selectedOrder])

	const isDenied = selectedOrder?.status?.status === 'DENIED'
	const isCanceled = selectedOrder?.status?.status === 'CANCELED'
	const isAborted = selectedOrder?.status?.status === 'ABORTED'
	const isAccepted = selectedOrder?.status?.status !== 'NOT_ACCEPTED'
	const isRegistered = selectedOrder?.status?.status !== 'NOT_REGISTERED'
	const isIssuedToCourier = selectedOrder?.status?.status === 'ON_THE_WAY'
	const isCompleted = selectedOrder?.status?.status === 'COMPLETED'
	const isClosed = !!selectedOrder?.zNumber
	const hasPlatform = !!selectedOrder?.platformMetaData
	const isRestaurant = selectedOrder?.orderType === 'RESTAURANT'
	const isNotPlatformOnlineShop = hasPlatform && selectedOrder?.platformMetaData?.platformType !== 'ONLINE_SHOP'
	const isEcTerminalOrder = selectedOrder?.payments?.find(p => p.method === 'EC_TERMINAL')
	const isMoneyExchange = selectedOrder?.items?.find(item => item.type === 'CASH_EXCHANGE')
	const isTicketOrder = !!selectedOrder?.ticketMetaData
	const orderDate = selectedOrder?.preOrder ? selectedOrder?.preOrder : selectedOrder?.orderDate
	const isPartiallyPaidWithGiftCoupon = !!selectedOrder?.payments?.find(p => p.method === 'GIFT_COUPON')
	const isUnpaidButPartiallyPaidWithCoupons = !isPaid && isPartiallyPaidWithGiftCoupon
	const isTodayOrder = checkIsTodayOrder(orderDate)
	const isGiftCouponItem = selectedOrder?.items?.filter((el) => el?.type === 'GIFT_COUPON')?.[0]
	const isCouponItem = selectedOrder?.items?.filter((el) => ORDER_RESTORE_HIDDEN_COUPON_TYPES.includes(el?.type))?.[0]

	const conditionForPrint = !isCanceled && !isDenied && isAccepted && isRegistered && isTodayOrder && !isAborted
	const conditionForChange = !isCanceled && !isDenied && !isNotPlatformOnlineShop && !isClosed && isRegistered && !isInvoiceOrder && !isMoneyExchange && !isUnpaidButPartiallyPaidWithCoupons && !isGiftCouponItem && !isAborted && !isTicketOrder
	const isRegisteredByCounters = !!selectedOrder?.orderDayCounter

	const buttons = [
		{ icon: 'show', text: t('order.modal.show_order'), onClick: showOrderDetails, show: true },
		{ icon: 'print', text: t('order.modal.print_order'), onClick: printOrderClick, show: conditionForPrint },
		{ icon: 'print', text: t('order.modal.print_order'), onClick: printCanceledOrderClick, show: isCanceled && !isClosed && isRegistered && isPaid },
		{ icon: 'cancel', text: t('order.modal.cancel_order'), onClick: cancelOrder, show: !isCanceled && !isDenied && isAccepted && canCancelOrder(selectedOrder) && isRegistered && hasRoleCancelOrder && isFullyPaidOrder(selectedOrder) && !isMoneyExchange },
		{ icon: 'change', text: t('order.modal.change'), onClick: changeOrder, show: conditionForChange },
		{ icon: 'change', text: t('order.modal.change_payment'), onClick: changePayment, show: !isCanceled && !isDenied && hasRoleServiceWrite && isAccepted && !isClosed && isRegistered && !isPlatformDriverOrder && isPaid && (!isInvoiceOrder || canCancelOrder(selectedOrder)) && !isMoneyExchange && !isNotPlatformOnlineShop && !isTicketOrder },
		{ icon: 'user', text: t(selectedOrder?.processedBy ? 'order.modal.change_driver' : 'order.modal.reserve_driver'), onClick: openSingleDriverModal, show: !isCanceled && !isDenied && isAccepted && !isClosed && isRegistered && !isPlatformDriverOrder && (isPaid || isDriverPaymentPending) && !isMoneyExchange },
		markReadyButton,
		{ icon: 'complete', text: t('order.modal.confirm_order'), onClick: confirmOrder, show: !isCanceled && !isDenied && hasPlatform && !isAccepted && !isClosed && isRegistered },
		{ icon: 'register', text: t('order.modal.register_order'), onClick: registerOrder, show: !isClosed && !isRegistered && canBeRegisteredNow },
		{ icon: 'complete', text: t('order.modal.complete_order'), onClick: completeOrder, show: !isCanceled && !isDenied && isAccepted && !isClosed && isRegistered && (!isPlatformDriverOrder || isTakeawayDrive || isFoodoraDrive) && isPaid && !isMoneyExchange },
		{ icon: 'check', text: t('order.modal.receipt'), onClick: handlePrintEntertainmentExpensesReceipt, show: !isCanceled && !isDenied && !isRestaurant && isAccepted && isRegistered && !isPlatformDriverOrder && (isPaid || isDriverCashPaymentPending) && !isMoneyExchange },
		{ icon: 'repeat', text: t('order.modal.recover_order'), onClick: restoreOrder, show: isCanceled && !selectedOrder?.restored && !isClosed && isRegistered && !isPlatformDriverOrder && !isEcTerminalOrder && !isInvoiceOrder && isPaid && !isGiftCouponItem && !isCouponItem && !isPartiallyPaidWithGiftCoupon && !isTicketOrder },
		{ icon: 'give-courier', text: t(`order.modal.give-courier.${selectedOrder?.platformMetaData?.platformType}`), onClick: passOrderToPlatformCourier, show: isPlatformDriverOrder && !isPartnerDriverOrder && !isTakeawayDrive && !isFoodoraDrive && !isCanceled && !isDenied && !isClosed && isRegistered && isAccepted && !isIssuedToCourier && !isCompleted },
		{ icon: 'payment', text: t('order.modal.order_pay'), onClick: handlePayment, show: !isCanceled && !isDenied && !isClosed && isRegistered && isAccepted && !isPaid && isTodayOrder && !isAborted },
		{ icon: 'give-courier', text: t('order.modal.give_another_courier'), onClick: passOrderToAnotherCourier, show: isDeliveryPromiseId && isDelivery && !isDeliveryByPlatform && !isCanceled && !isDenied && !isClosed && isRegistered && isAccepted && !isIssuedToCourier },
		{ icon: 'abort', text: t('order.modal.abort_order'), onClick: abortOrder, show: !isCanceled && !isDenied && !isClosed && isRegistered && isAccepted && !isPaid && isTodayOrder && isRegisteredByCounters && !isAborted },
		goToInvoiceButton,
		createInvoiceButton,
	]

	const sortedButtons = isPaid ? buttons : reorderButtonsForUnpaidOrder(buttons, t)

	return (
		<>
			<ButtonsModal isOpen={showOrder}
										close={handleClose}
										id="delivery-order-buttons-modal"
										size="small"
										title={(selectedOrder?.orderDayCounter ?
											t('order.modal.number_order') + ' #' + selectedOrder?.orderDayCounter
											: t('order.modal.without_number'))}
										titlePrice={selectedOrder?.totalPrice}
										zIndex={EVENT_Z_INDEX}
										buttons={sortedButtons} />
			<InvoiceCustomerDataModal {...recipientModalProps} />
		</>
	)
})

CustomerOrderButtonsModal.displayName = 'CustomerOrderButtonsModal'
export default CustomerOrderButtonsModal

